import { ActionReducerMapBuilder, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../../../../app/store";
import { APIError } from "../../../../../services/HttpService";
import {
  getQueryParamAggregation,
  getUrlsWithParam,
  QueryParamAggregation,
} from "../../../../../services/QueryParamService";
import { RejectError, RejectErrorType } from "../../../../error/Error";

export type ParamUrlsView = {
  urls: string[];
  offset: number;
  hasMore: boolean;
  loading: boolean;
  error?: string;
};

export type QueryParamFinderState = {
  userId: number | null;
  aggregation: QueryParamAggregation | null;
  aggregationLoading: boolean;
  aggregationError: string | null;
  paramUrls: Record<string, ParamUrlsView>;
};

const initialState: QueryParamFinderState = {
  userId: null,
  aggregation: null,
  aggregationLoading: false,
  aggregationError: null,
  paramUrls: {},
};

const rejection = (error: unknown, rejectWithValue: (value: RejectError) => unknown) => {
  if (error instanceof APIError) {
    return rejectWithValue({ message: error.message, code: error.statusCode, type: RejectErrorType.API });
  }
  throw error;
};

export const loadAggregation = createAsyncThunk<QueryParamAggregation, { userId: number }, { rejectValue: RejectError }>(
  "queryParamFinder/aggregation",
  async ({ userId }, { rejectWithValue }) => {
    try {
      return await getQueryParamAggregation(userId);
    } catch (error) {
      return rejection(error, rejectWithValue) as never;
    }
  }
);

export type ParamUrls = { parameter: string; offset: number; urls: string[]; hasMore: boolean };

export const loadParamUrls = createAsyncThunk<
  ParamUrls,
  { userId: number; parameter: string; offset: number },
  { rejectValue: RejectError }
>("queryParamFinder/paramUrls", async ({ userId, parameter, offset }, { rejectWithValue }) => {
  try {
    const page = await getUrlsWithParam(userId, parameter, offset);
    return { parameter, offset, urls: page.urls, hasMore: page.hasMore };
  } catch (error) {
    return rejection(error, rejectWithValue) as never;
  }
});

const failureMessage = (fallback: string, payload?: RejectError): string => payload?.message ?? fallback;

const isStale = (state: QueryParamFinderState, userId: number): boolean => state.userId !== userId;

const aggregationReducers = (builder: ActionReducerMapBuilder<QueryParamFinderState>) => {
  builder.addCase(loadAggregation.pending, (draft, action) => {
    draft.userId = action.meta.arg.userId;
    draft.aggregationLoading = true;
    draft.aggregationError = null;
  });
  builder.addCase(loadAggregation.fulfilled, (draft, action) => {
    if (isStale(draft, action.meta.arg.userId)) return;
    draft.aggregation = action.payload;
    draft.aggregationLoading = false;
  });
  builder.addCase(loadAggregation.rejected, (draft, action) => {
    if (isStale(draft, action.meta.arg.userId)) return;
    draft.aggregationLoading = false;
    draft.aggregationError = failureMessage("Failed to load query parameters", action.payload);
  });
};

const urlsReducers = (builder: ActionReducerMapBuilder<QueryParamFinderState>) => {
  builder.addCase(loadParamUrls.pending, (draft, action) => {
    if (isStale(draft, action.meta.arg.userId)) return;
    const { parameter, offset } = action.meta.arg;
    const previous = ownValue(draft.paramUrls, parameter);
    draft.paramUrls[parameter] = {
      urls: previous?.urls ?? [],
      offset,
      hasMore: previous?.hasMore ?? false,
      loading: true,
    };
  });
  builder.addCase(loadParamUrls.fulfilled, (draft, action) => {
    if (isStale(draft, action.meta.arg.userId)) return;
    const { parameter, offset, urls, hasMore } = action.payload;
    draft.paramUrls[parameter] = { urls, offset, hasMore, loading: false };
  });
  builder.addCase(loadParamUrls.rejected, (draft, action) => {
    if (isStale(draft, action.meta.arg.userId)) return;
    const view = ownValue(draft.paramUrls, action.meta.arg.parameter);
    if (view) {
      view.loading = false;
      view.error = failureMessage("Failed to load URLs", action.payload);
    }
  });
};

export const queryParamFinderSlice = createSlice({
  name: "queryParamFinder",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    aggregationReducers(builder);
    urlsReducers(builder);
  },
});

// ponytail: a param literally named constructor/__proto__/toString must read
// undefined, not the inherited Object member a bare index access would return.
export function ownValue<T>(map: Record<string, T>, key: string): T | undefined {
  return Object.hasOwn(map, key) ? map[key] : undefined;
}

export const queryParamFinderState = (state: RootState): QueryParamFinderState => state.queryParamFinder;

export default queryParamFinderSlice.reducer;
